/**
 * @fileoverview Inline "Schedule Follow-up" form for TenantDetailModal.
 * @description Rendered for the target's current owner or an authorised reporting Manager/Head
 * (enforced again, defense-in-depth, by the mock API itself). Records `Target.nextFollowUpAt`,
 * shown read-only elsewhere in this same modal's target summary and in My Page's "Next Follow-up"
 * column/Upcoming Activities section.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { useScheduleFollowUp } from '@/features/sales/hooks/useScheduleFollowUp'
import {
  scheduleFollowUpFormSchema,
  type ScheduleFollowUpFormValues,
} from '@/features/sales/schemas/schedule-follow-up-form.schema'
import Label from '@/shared/components/atoms/Label'

import DialogActions from './DialogActions'

import type { FormEvent } from 'react'
import type { UseFormRegisterReturn } from 'react-hook-form'

interface ScheduleFollowUpFormProps {
  tenantId: string
  targetId: string
  currentNextFollowUpAt: string | null
  actingUserId: string
  actingUserRole: string
  onUpdated: () => void
  onCancel: () => void
}

/**
 * @description The follow-up date/time input with its validation message.
 * @param {{ registration: UseFormRegisterReturn; error: string | undefined }} props - The input's
 * react-hook-form registration and its translated error, if any.
 * @param {UseFormRegisterReturn} props.registration - The `register('nextFollowUpAt')` result.
 * @param {string | undefined} props.error - The translated validation message.
 */
function FollowUpDateTimeField({
  registration,
  error,
}: {
  registration: UseFormRegisterReturn
  error: string | undefined
}) {
  const { t } = useTranslation()

  return (
    <div>
      <Label htmlFor="nextFollowUpAt">{t('sales.tenantDetail.followUp.label')}</Label>
      <input
        id="nextFollowUpAt"
        data-testid="schedule-follow-up-datetime-input"
        type="datetime-local"
        className="mt-1.5 h-[38px] w-full rounded-[8px] border border-input-border bg-white px-3 text-sm font-medium text-ink focus:outline-none focus:ring-2 focus:ring-primary/20"
        {...registration}
      />
      {error && (
        <span
          className="mt-1 block text-[11.5px] font-medium text-danger"
          data-testid="schedule-follow-up-datetime-error"
        >
          {error}
        </span>
      )}
    </div>
  )
}

/**
 * @component
 * @param {ScheduleFollowUpFormProps} props - The target to update and the acting user's identity.
 * @param {string} props.tenantId - The tenant the target belongs to, for query invalidation.
 * @param {string} props.targetId - The target id to update.
 * @param {string | null} props.currentNextFollowUpAt - The target's current follow-up, as the
 * form's default.
 * @param {string} props.actingUserId - The signed-in viewer's sales-roster id.
 * @param {string} props.actingUserRole - The signed-in viewer's active role name.
 * @param {() => void} props.onUpdated - Called after a successful update.
 * @param {() => void} props.onCancel - Called to dismiss the form without updating.
 */
function ScheduleFollowUpForm({
  tenantId,
  targetId,
  currentNextFollowUpAt,
  actingUserId,
  actingUserRole,
  onUpdated,
  onCancel,
}: ScheduleFollowUpFormProps) {
  const { t } = useTranslation()
  const scheduleFollowUp = useScheduleFollowUp(tenantId)
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ScheduleFollowUpFormValues>({
    resolver: zodResolver(scheduleFollowUpFormSchema),
    defaultValues: { nextFollowUpAt: currentNextFollowUpAt?.slice(0, 16) ?? '' },
  })

  /**
   * @description Submits the follow-up date/time.
   * @param {ScheduleFollowUpFormValues} values - The validated form values.
   */
  const onValid = (values: ScheduleFollowUpFormValues) => {
    scheduleFollowUp.mutate(
      {
        targetId,
        input: { nextFollowUpAt: values.nextFollowUpAt, actingUserId, actingUserRole },
      },
      { onSuccess: onUpdated },
    )
  }

  /**
   * @description Runs the schema validation and, if valid, calls onValid.
   * @param {FormEvent<HTMLFormElement>} event - The form submit event.
   */
  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    void handleSubmit(onValid)(event)
  }

  return (
    <form
      onSubmit={onSubmit}
      noValidate
      data-testid="schedule-follow-up-form"
      className="flex flex-col gap-3 rounded-lg border border-row-border bg-surface-hover p-3.5"
    >
      <span className="text-[12.5px] font-bold text-ink" data-testid="schedule-follow-up-title">
        {t('sales.tenantDetail.followUp.title')}
      </span>

      <FollowUpDateTimeField
        registration={register('nextFollowUpAt')}
        error={errors.nextFollowUpAt && t(errors.nextFollowUpAt.message ?? '')}
      />

      <DialogActions
        testIdPrefix="schedule-follow-up"
        cancelLabel={t('sales.pipeline.form.cancel')}
        confirmLabel={t('sales.tenantDetail.followUp.submit')}
        onCancel={onCancel}
        confirmType="submit"
        isLoading={scheduleFollowUp.isPending}
        disabled={false}
      />

      {scheduleFollowUp.isError && (
        <p
          role="alert"
          className="text-[12.5px] text-danger"
          data-testid="schedule-follow-up-error"
        >
          {scheduleFollowUp.error instanceof Error
            ? scheduleFollowUp.error.message
            : String(scheduleFollowUp.error)}
        </p>
      )}
    </form>
  )
}

export default ScheduleFollowUpForm
